import type { Skill } from '../content/types.ts';
import type { Character } from '../domain/character.ts';
import type { HotlistCatalogue, HotlistOwnership } from './hotlist.ts';
import { hotlistSlotStatus } from './hotlist.ts';

/**
 * RULE DCC-SPELL-001 - what a Spell IS, asked in one place.
 *
 * `kind === 'spell'`, and nothing else. NOT "has a Mana cost" and NOT "makes an
 * Attack": Heal is a Spell that does neither an Attack nor a Check, and an
 * Item could carry a cost without being one. Category is `kind`'s job
 * (RULE DCC-SKILL-002).
 */
export function isSpell(skill: Skill): boolean {
  return skill.kind === 'spell';
}

/** RULE DCC-SKILL-002 - a Spell that needs no Skill Check, such as Heal. */
export function isPassiveSpell(skill: Skill): boolean {
  return isSpell(skill) && skill.traits.includes('passive');
}

/** Explicit homebrew manual casting: account for costs, leave described effects to the GM. */
export function isManualCustomSpell(skill: Skill): boolean {
  return isPassiveSpell(skill) && skill.source.book === 'custom' && skill.details?.checkType === 'manual' && skill.supportEffect === undefined;
}

/**
 * How many Actions casting this known Spell costs right now: ONE or TWO.
 *
 * **PROJECT RULING — the printed rules are ambiguous.** Rules p.19, Table 2
 * says "Choose a Spell or scroll **in your Hotlist**", which reads as a
 * requirement; C&C p.15 calls the Hotlist "easy access… as needed", and RULE
 * DCC-HOT-018 says membership grants nothing mechanically. Both readings are
 * recorded in `docs/RULES_CONFLICTS.md`. The project ruling is that a crawler
 * may cast any Spell they know, and the Hotlist buys SPEED rather than
 * permission:
 *
 *   in a valid Hotlist slot  -> 1 Action
 *   known but not prepared   -> 2 Actions
 *
 * **THIS IS A PROPERTY OF THE CRAWLER, NOT OF THE BUTTON THEY PRESSED.**
 * Selecting a Hotlisted Fireball from the Attack panel costs one Action just as
 * clicking its Hotlist slot does, and a Hotlist shortcut can never manufacture
 * a discount for a Spell that is no longer validly prepared. So no caller
 * supplies the cost or a `fromHotlist` flag; every pathway asks this, and the
 * service re-asks it at execution time because a slot can change while a screen
 * is open.
 *
 * VALIDITY MATTERS, not merely a matching ID. `hotlistSlotStatus` returns
 * `unresolved` for a Spell this installation lacks and `unavailable` for a slot
 * that cannot be honoured; neither is preparation, so neither earns the
 * discount. That is the same rule that keeps a malformed slot inert everywhere
 * else.
 */
export function spellCastActionCost(
  character: Character,
  skillId: string,
  catalogue: HotlistCatalogue,
): 1 | 2 {
  // A Spell slot's status does not consult carried copies, but it DOES ask
  // whether the crawler knows the Skill - an unknown Spell in a slot is not
  // preparation, and must not earn the discount.
  const ownership: HotlistOwnership = {
    availableCopies: () => 0,
    knowsSkill: (id) => character.skills.some((entry) => entry.skillId === id),
  };
  const prepared = character.hotlist.some((entry) => {
    if (entry === null || entry === undefined) return false;
    if (entry.kind !== 'spell' || entry.skillId !== skillId) return false;
    return hotlistSlotStatus(entry, catalogue, ownership).state === 'ok';
  });
  return prepared ? 1 : 2;
}

/** Whether a Spell slot may be Cast from directly. */
export function spellCastAvailable(
  status: { state: string; kind?: string },
  skill: Skill | undefined,
): boolean {
  if (status.state !== 'ok' || status.kind !== 'spell') return false;
  return skill !== undefined && isSpell(skill);
}

/**
 * RULE DCC-ENC-005 - may this Spell be cast as a Healing Interrupt?
 *
 * SHARED between the service, which ENFORCES it, and the panels, which decide
 * whether to offer a button. Two copies of an eligibility rule is one waiting
 * to offer a control the service refuses.
 *
 * From AUTHORED DATA ONLY. Never the name, the ID, the description or the Mana
 * cost. All FOUR clauses are load-bearing - Passive included, because a Spell
 * that makes a Skill Check belongs to the Attack pathway and cannot be resolved
 * here at all. The other two: Protective Shell and Shield carry the
 * `interrupt` trait (C&C pp.39-40) with mechanics nobody has written, so the
 * trait alone is not permission; and a healing Spell without the trait is an
 * ordinary Action, not a reaction.
 *
 * A grey button is an explanation, not a boundary - `castSupportSpell` checks
 * this again on its own and refuses a direct call regardless of what any panel
 * would have shown.
 */
export function isHealingInterruptSpell(skill: Skill): boolean {
  return (
    // Spell AND Passive, through the existing helper rather than a second copy
    // of the trait logic. THIS CLAUSE WAS MISSING and the omission was invisible
    // in the only place it mattered: `castSupportSpell` rejects a Spell that
    // makes a Check earlier on its own, so the mechanical pathway was safe while
    // the SHARED helper still said "yes" to a Spell the service would refuse.
    // A helper whose whole job is to keep the panels and the service agreeing
    // must not be the one thing that disagrees with them.
    isPassiveSpell(skill) &&
    skill.traits.includes('interrupt') &&
    skill.supportEffect?.kind === 'heal-slots'
  );
}
